<script lang="ts" setup>
import { useTimeoutFn } from '@vueuse/core'
import type { Product, ResponseData } from '@/types'

const router = useRouter()
const route = useRoute()
const { t: $t, locale } = useI18n()
const { axiosRequest } = useAxios()
const page = ref(1)
const search = ref('')

const params = computed(() => {
  return {
    page: page.value,
    search: search.value
  }
})

const { href } = useCanonical()

const data = ref<ResponseData<Product[]>>()

const { start: startRequest, stop: stopRequest } = useTimeoutFn(() => {
  loadData()
}, 1500)

async function loadData() {
  const { data: responData }: { data: ResponseData<Product[]> } = await axiosRequest.get(
    `/other-products?lang=${locale.value}`,
    {
      params: params.value
    }
  )
  data.value = responData
  stopRequest()
}

function replaceWindowUrl() {
  router.replace(`/activities/single?page=${page.value}&search=${search.value}`)

  startRequest()
}

watch(
  () => search.value,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      page.value = 1
    }

    replaceWindowUrl()
  }
)

watch(
  () => page.value,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      replaceWindowUrl()
    }
  }
)

onMounted(() => {
  stopRequest()
  if (route.query.page) {
    page.value = Number(route.query?.page as unknown as number)
  }
  if (route.query.search) {
    search.value = route.query?.search as unknown as string
  }

  loadData()
})

useHead({
  link: [
    {
      rel: 'canonical',
      href
    }
  ]
})

useSeoMeta({
  title: $t('meta.activities_others.title'),
  ogTitle: $t('meta.activities_others.title'),
  description: $t('meta.activities_others.description'),
  ogDescription: $t('meta.activities_others.description'),
  twitterDescription: $t('meta.activities_others.description'),
  twitterTitle: $t('meta.activities_others.title'),
  ogUrl: href,
  ogType: 'website'
})

useServerSeoMeta({
  title: $t('meta.activities_others.title'),
  ogTitle: $t('meta.activities_others.title'),
  description: $t('meta.activities_others.description'),
  ogDescription: $t('meta.activities_others.description'),
  twitterDescription: $t('meta.activities_others.description'),
  twitterTitle: $t('meta.activities_others.title'),
  ogUrl: href,
  ogType: 'website'
})
</script>
<template>
  <div class="space-y-32">
    <div class="relative">
      <div class="absolute w-full z-10 pt-64">
        <Container>
          <div class="grid place-items-center">
            <div class="max-w-2xl text-center">
              <div class="space-y-6">
                <div class="text-white text-2xl">
                  {{ $t('other.sub_title') }}
                </div>
                <h1 class="text-3xl md:text-[40px] md:leading-[49.1px] text-white">
                  {{ $t('packages.title') }}
                </h1>
                <div class="text-lg leading-5 md:text-xl text-white font-general-sans">
                  {{ $t('packages.desc') }}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>
      <div class="-mt-20">
        <div class="relative">
          <div class="absolute h-full w-full bg-gradient-to-t from-black/50 to-black/80"></div>
          <img src="/combo-hero.jpg" alt="homepage hero" class="w-full h-full min-h-[680px] object-cover" />
        </div>
      </div>
    </div>

    <div>
      <Container>
        <div class="space-y-8">
          <div class="font-general-sans text-2xl font-medium flex items-center">
            {{ $t('other.sub_title') }}
            <Icon name="i-heroicons-arrow-right" class="rotate-45" />
          </div>
          <div class="text-3xl md:text-[40px] md:leading-[49.1px] max-w-lg">
            {{ $t('other.description') }}
          </div>
          <div class="space-y-8">
            <template v-for="(item, index) in data?.data" :key="index + 'p'">
              <PackageSlide :product="item" />
            </template>
          </div>
          <div v-if="data?.meta">
            <Pagination
              v-model="page"
              :total="(data?.meta.total as number)"
              :per-page="(data?.meta.per_page as number)"
              :include-first-last="false"
            />
          </div>
        </div>
      </Container>
    </div>
    <div></div>
  </div>
</template>

<style></style>
